import React, { useMemo, useState, useRef, useEffect } from "react";
import arePropsEqual from "../../utils/arePropsEqual";
import TickerService from "../../packs/services/convertflow/elements/ticker_service";
import { buildTickerStylesheet } from "./ElementTickerStyleResolver";
import BuilderStyleTag from "../shared/BuilderStyleTag";

const ElementTicker = (props) => {
  const element = props.element;
  const variant = props.variant || { options: {} };
  const device = props.device;
  const cta = props.cta;
  const [isPaused, setIsPaused] = useState(false);
  const wrapperRef = useRef(null);
  const [wrapperWidth, setWrapperWidth] = useState(0);

  useEffect(() => {
    if (!wrapperRef.current) return undefined;
    let frameId = null;
    const measure = () => {
      if (frameId !== null) return;
      frameId = requestAnimationFrame(() => {
        frameId = null;
        if (!wrapperRef.current) return;
        const next = wrapperRef.current.offsetWidth || 0;
        setWrapperWidth((prev) => (prev === next ? prev : next));
      });
    };

    measure();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(measure);
      observer.observe(wrapperRef.current);
      return () => {
        if (frameId !== null) cancelAnimationFrame(frameId);
        observer.disconnect();
      };
    }
    window.addEventListener("resize", measure);

    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      window.removeEventListener("resize", measure);
    };
  }, []);

  let alignmentClass = "cf-outer-center";
  const align =
    device === "mobile"
      ? element.options["cf-alignment-mobile"]
      : element.options["cf-alignment"];
  if (align) {
    if (align.indexOf("left") > -1) alignmentClass = "cf-outer-left";
    else if (align.indexOf("right") > -1) alignmentClass = "cf-outer-right";
  }

  const classes = `element-content cf-ticker-element ${alignmentClass}`;

  const gap = parseInt(
    TickerService.resolveNumericOption(
      element.options,
      variant.options,
      "ticker-image-gap",
      device,
      20
    )
  );

  const maxHeight = parseInt(
    TickerService.resolveNumericOption(
      element.options,
      variant.options,
      "ticker-max-image-height",
      device,
      120
    )
  );

  const maxWidth = parseInt(
    TickerService.resolveNumericOption(
      element.options,
      variant.options,
      "ticker-max-image-width",
      device,
      300
    )
  );

  const tickerImages = useMemo(
    () =>
      TickerService.imagesFromToggleItems(
        element.content_toggle_items,
        element.id
      ),
    [element.content_toggle_items, element.id]
  );

  const numImages = tickerImages.length;
  const scrollDistance = numImages * (maxWidth + gap);

  const cloneSetCount =
    wrapperWidth > 0 && scrollDistance > 0
      ? Math.max(1, Math.ceil((wrapperWidth * 2) / scrollDistance))
      : 1;

  const stylesheet = buildTickerStylesheet(
    props.builder,
    device,
    cta,
    element,
    variant
  );

  const renderSlots = (keyPrefix) =>
    tickerImages.map((img, index) => (
      <div
        key={`${keyPrefix}${index}`}
        className="cf-ticker-image-slot"
        aria-hidden={keyPrefix !== "" ? "true" : undefined}
      >
        {img.url ? (
          <img
            src={img.url}
            alt={img.alt || ""}
            className="cf-ticker-image"
            draggable={false}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              background: "#e5e7eb",
              borderRadius: 4,
            }}
          />
        )}
      </div>
    ));

  if (numImages === 0) {
    return (
      <React.Fragment>
        <div className={classes}>
          <BuilderStyleTag
          builder={props.builder}
          styleKey={`cta:${cta && cta.id}:element:${element.id}:ticker`}
          stylesheet={stylesheet}
        />
          <div className="cf-ticker-wrapper">
            <div
              className="cf-ticker-track"
              style={{
                justifyContent: "center",
                height: maxHeight + "px",
              }}
            >
              <span
                style={{
                  color: "#9ca3af",
                  fontSize: "13px",
                  fontStyle: "italic",
                  padding: "0 16px",
                }}
              >
                Upload images to get started
              </span>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }

  const trackClass = `cf-ticker-track${isPaused ? " cf-ticker-paused" : ""}`;

  return (
    <React.Fragment>
      <div
        className={classes}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <BuilderStyleTag
          builder={props.builder}
          styleKey={`cta:${cta && cta.id}:element:${element.id}:ticker`}
          stylesheet={stylesheet}
        />

        <div className="cf-ticker-wrapper" ref={wrapperRef}>
          <div className={trackClass} data-ticker-id={element.id}>
            {renderSlots("")}

            {Array.from({ length: cloneSetCount }).map((_, i) =>
              renderSlots(`clone-${i}-`)
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default React.memo(ElementTicker, arePropsEqual);
